import { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ContractLined, DocAddFilled, DocTableFilled, LineChartArrowFilled } from '@tag/tag-icons';

import { useParams } from 'react-router-dom';
import cn from 'classnames';

import { GridApi, RowSelectedEvent } from 'ag-grid-community';

import { useRecoilState } from 'recoil';

import Pagination from '@/components/common/Pagination/Pagination';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { useColumns } from './columns';
import TagTabs from '@/components/common/TagTabs/TagTabs';
import AgGridLoadingOverlay
  from '@/components/common/TableGrid/CustomComponents/AgGridLoadingOverlay/AgGridLoadingOverlay';
import useGetVarBillingUsageDetails from '../hooks/useGetVarBillingUsageDetails';
import { USAGE_RECORDS_PER_PAGE } from '@/config/common.config';
import useGetVarBillingUsageDetailsTotalCount from '../hooks/useGetVarBillingUsageDetailsTotalCount';
import {
  errorStatuses, getDynamicColumns, getRowsImportedText, getRowsMatchedText, getTotalValueImported,
  getTotalValueMatchedCalculated,
  matchingTriggeredStatuses
} from '../helpers/getUsageDetailsHelpers';
import Skeleton from '@/components/common/Skeleton/Skeleton';
import styles from '../file-details.module.scss';
import { VarBillingUsageDetailOutput, VarBillingUsageHeaderStatusEnum } from
  '@/graphql/contracts/_generated_/contracts';
import Button from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import useGetVarBillingUsageDetailsStats from '../hooks/useGetVarBillingUsageDetailsStats';
import useExport from '../hooks/export/useExport';
import {
  SelectedRow,
  SelectionEventInitiator,
  useGridSelectionSubscription
} from '@/pages/ContractLines/hooks/useGridSelectionSubscription';
import { UsageLineSelectionPayload, clearLinesSelection, variableLinesSelectionObservable } from
  './usageDetailsSelectionObservable';
import RightPanelContainer from '../ActionsPanel/RightPanel.container';
import { checkedIds } from './checkedIds.state';
import { TabKeys, statusesMap, tabs } from '../../types';

interface UsageDetailsProps {
  usageHeaderID?: string;
  usageHeaderStatus?: VarBillingUsageHeaderStatusEnum | null;
  getVarBillingUsageHeader: (documentID: string) => void
  usageHeaderFileName?: string | null;
  usageHeaderModified: boolean;
  resetForm: () => void;
}

export function UsageDetails(props: UsageDetailsProps) {
  const { usageHeaderID, usageHeaderStatus, usageHeaderFileName,
    getVarBillingUsageHeader, usageHeaderModified, resetForm } = props;
  const gridRef = useRef<AgGridReact>(null);
  const columns = useColumns();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { fileId }: { fileId: string } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<TabKeys>(TabKeys.ALL);
  const status = activeTab !== TabKeys.ALL && statusesMap[activeTab]
    ? { containsValues: statusesMap[activeTab] }
    : undefined;
  const {
    getVarBillingUsageDetails,
    isCalledUsageDetails,
    varBillingUsageDetails,
    isLoadingUsageDetails,
  } = useGetVarBillingUsageDetails();

  const {
    usageStats,
    getVarBillingUsageDetailsStats,
    isLoadingUsageDetailsStats
  } = useGetVarBillingUsageDetailsStats();

  const {
    totalCount,
    getVarBillingUsageDetailsTotalCount,
    isLoadingUsageDetailsTotalCount
  } = useGetVarBillingUsageDetailsTotalCount();
  const [checkedRowIds, setCheckedRowIds] = useRecoilState(checkedIds);

  const { exportUsageDetails } = useExport();

  const totalPages = Math.ceil(totalCount / USAGE_RECORDS_PER_PAGE);
  const matchingWasTrigged = Boolean(usageHeaderStatus && matchingTriggeredStatuses.includes(usageHeaderStatus));
  const showTotalNetValue = usageStats?.totalNet &&
    usageHeaderStatus !== VarBillingUsageHeaderStatusEnum.ImportError;
  const showMatchedNetValue = usageStats?.matchedNet && matchingWasTrigged;
  const statusMessage = useMemo(() => varBillingUsageDetails?.find((col: VarBillingUsageDetailOutput) =>
    col?.statusMessage), [varBillingUsageDetails]);
  const ignoreComment = useMemo(() => varBillingUsageDetails?.find((col: VarBillingUsageDetailOutput) =>
    col?.ignoreComment), [varBillingUsageDetails]);

  const onTabChange = (event: CustomEvent) => {
    const tabName = event?.detail?.name as TabKeys;
    setActiveTab(tabName);
    setCurrentPage(1);
  };

  const refreshPageData = () => {
    getVarBillingUsageHeader(fileId);
    getVarBillingUsageDetails({
      filter: {
        headerPrimaryKey: { eq: usageHeaderID },
        status
      },
      after: currentPage
    });
    getVarBillingUsageDetailsTotalCount({
      headerPrimaryKey: { eq: usageHeaderID },
      status
    });
    getVarBillingUsageDetailsStats({
      headerPrimaryKey: { eq: usageHeaderID },
    });
  };

  const handlePageRefresh = () => {
    return usageHeaderModified ? setShowConfirmationModal(true) : refreshPageData();
  };
  const handleExportToExcel = () => {
    if (usageHeaderID) {
      const filterStringified = JSON.stringify({ headerPrimaryKey: { eq: usageHeaderID } });
      const columnsNameAndDisplayName = getDynamicColumns(columns, statusMessage, matchingWasTrigged, ignoreComment)
        .filter(col => col.headerName).map(col => {
          return col.field && col.headerName ? ({
            name: col.field,
            displayName: col.headerName
          }) : null;

        });
      const fileName = usageHeaderFileName && usageHeaderStatus ?
        `${usageHeaderFileName.replace(/\.csv|\.xlsx/, '')} - ${usageHeaderStatus}.xlsx` :
        'Usage details.xlsx';

      exportUsageDetails({
        format: 'Xlsx',
        form: 'UsageDetails',
        filter: filterStringified,
        columns: columnsNameAndDisplayName
      }, fileName);
    }
  };

  const handleIgnoreOrRestoreAction = (redirectTabName: TabKeys) => {
    if (activeTab === TabKeys.ALL && activeTab === redirectTabName) {
      setCurrentPage(1);
      getVarBillingUsageDetails({
        filter: {
          headerPrimaryKey: { eq: usageHeaderID },
          status
        },
        after: currentPage
      });
      getVarBillingUsageDetailsTotalCount({
        headerPrimaryKey: { eq: usageHeaderID },
        status
      });
    } else {
      setCurrentPage(1);
      setActiveTab(redirectTabName);
    }
  };

  useEffect(() => {

    const setOverlay = () => {
      if (isLoadingUsageDetails || !isCalledUsageDetails) {
        return gridRef.current?.api?.showLoadingOverlay();
      }

      if (!varBillingUsageDetails?.length && isCalledUsageDetails) {
        return gridRef.current?.api?.showNoRowsOverlay();
      }

      return gridRef.current?.api?.hideOverlay();
    };

    setOverlay();


  }, [isLoadingUsageDetails, varBillingUsageDetails, isCalledUsageDetails]);

  useEffect(() => {
    if (!usageHeaderID) return;

    getVarBillingUsageDetails({
      filter: {
        headerPrimaryKey: { eq: usageHeaderID },
        status
      },
      after: currentPage
    });

    getVarBillingUsageDetailsTotalCount({
      headerPrimaryKey: { eq: usageHeaderID },
      status
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageHeaderID, currentPage, activeTab, usageHeaderStatus]);

  useEffect(() => {
    if (usageHeaderID) {
      getVarBillingUsageDetailsStats({
        headerPrimaryKey: { eq: usageHeaderID }
      });

    }

    setCheckedRowIds([]);
    clearLinesSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageHeaderID]);

  const getSelectedUsageRows = (api: GridApi<VarBillingUsageDetailOutput>) => {
    api.getRenderedNodes().forEach(node => {
      const nodeId = node?.data?.usageDetailID as string;
      if (node.isSelected() && !checkedRowIds.includes(nodeId)) {
        setCheckedRowIds(ids => [...ids, nodeId]);
      } else if (!node.isSelected() && checkedRowIds.includes(nodeId)) {
        setCheckedRowIds(checkedRowIds.filter(id => id !== nodeId));
      }
    });
    const selectedRows = api.getSelectedRows().map(cl => ({
      id: cl.usageDetailID,
      payload: {
        usageDetailsLine: cl
      }
    }));
    return selectedRows;
  };
  useGridSelectionSubscription({
    gridInstance: gridRef,
    observable: variableLinesSelectionObservable,
    dependenciesToInvalidateSelection: []
  });

  const onRowSelected = ({ api }: RowSelectedEvent<VarBillingUsageDetailOutput>) => {
    variableLinesSelectionObservable.next({
      initiator: SelectionEventInitiator.GRID,
      selectedGridRows: getSelectedUsageRows(api) as SelectedRow<UsageLineSelectionPayload>[]
    });
  };

  return (
    <>
      <AgConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        closeOnConfirm={false}
        onConfirm={() => {
          refreshPageData();
          resetForm();
          setShowConfirmationModal(false);
        }}
        title='Unsaved changes in Usage Header'
      >
        You have some unsaved changed in the Usage Header. <br />
        Press 'Cancel' to go back and save the changes or confirm you wish to continue and refresh the page.
      </AgConfirmationModal>
      {usageHeaderID && (
        <div className={styles['imported-stats-wrapper']}>
          <div className={styles['stats-section']}>
            <DocTableFilled />
            {isLoadingUsageDetailsStats ? (
              <Skeleton width={160} height={20} count={1} />
            ) : usageStats && (
              <div className={styles['stats-text']}>
                {getRowsImportedText(usageStats.totalUsageRecordsCount)}</div>
            )}
          </div>
          <div className={styles['stats-section']}>
            <LineChartArrowFilled />
            {isLoadingUsageDetailsStats ? (
              <Skeleton width={160} height={20} count={1} />
            ) : usageStats && (
              <div className={styles['stats-text']}>
                {getRowsMatchedText(usageStats.matchedUsageRecordsCount)}</div>
            )}
          </div>
          {showTotalNetValue && (<div className={styles['stats-section']}>
            <ContractLined />
            {isLoadingUsageDetailsStats ? (
              <Skeleton width={160} height={20} count={1} />
            ) : usageStats && (
              <div className={styles['stats-text']}>
                {getTotalValueImported(usageStats.totalNet)}</div>
            )}
          </div>)}
          {showMatchedNetValue && (<div className={styles['stats-section']}>
            <DocAddFilled />
            {isLoadingUsageDetailsStats ? (
              <Skeleton width={160} height={20} count={1} />
            ) : usageStats && (
              <div className={styles['stats-text']}>
                {getTotalValueMatchedCalculated(usageStats.matchedNet)}</div>
            )}
          </div>)}
          {isLoadingUsageDetailsStats ? (
            <Skeleton width={160} height={20} count={1} />
          ) : usageStats && (
            <Button buttonSize="small" onClick={handlePageRefresh}
              className={cn(styles['small-btn'], 'mr-10')}>
              Refresh the page
            </Button>
          )}
        </div>
      )}
      <TagTabs
        tabs={tabs}
        accent="viridiangreen"
        onTabChange={onTabChange}
        activeTabKey={activeTab}
        headersContainerStyle={{
          backgroundColor: '#F7F8FA',
          height: '55px',
          borderTop: '1px solid #e2ecef',
          width: '100%'
        }}
        headerContainerStyle={{ height: '55px' }}
        addQueryToUrl={false}
        cacheMode="no-cache"
        disabled={isLoadingUsageDetails || isLoadingUsageDetailsTotalCount}
      />

      <TableGrid<VarBillingUsageDetailOutput>
        ref={gridRef}
        columnDefs={getDynamicColumns(columns, statusMessage, matchingWasTrigged, ignoreComment)}
        rowData={varBillingUsageDetails}
        suppressMovableColumns={true}
        loadingOverlayComponent={AgGridLoadingOverlay}
        domLayout="autoHeight"
        rowSelection="multiple"
        suppressRowClickSelection={true}
        onRowSelected={onRowSelected}
        rowClassRules={{
          [styles['row-fail']]: (params) => params.data?.status ? errorStatuses.includes(params.data.status) : false,
        }}
        defaultColDef={{ sortable: false, resizable: false }}
      />

      {!isLoadingUsageDetails && !isLoadingUsageDetailsTotalCount && totalPages > 1 && (
        <div className={styles['usage-records-pagination']}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onClickPage={setCurrentPage}
          />
        </div>
      )}
      <RightPanelContainer
        onExcelExport={handleExportToExcel}
        onIgnoreOrRestoreAction={handleIgnoreOrRestoreAction} />
    </>
  );
}
